import React, { useState } from 'react';
import { PlusIcon, ClockIcon, MessageSquareIcon, GitBranchIcon, TrashIcon, PlayIcon, SettingsIcon, MoreHorizontalIcon } from 'lucide-react';
import { Workflow, WorkflowStep } from '../../types/whatsapp';
const MOCK_WORKFLOWS: Workflow[] = [{
  id: '1',
  name: 'New Lead Welcome Sequence',
  active: true,
  trigger: 'Lead Created (Source: WhatsApp)',
  steps: [{
    id: 's1',
    type: 'message',
    label: 'Send Welcome Message',
    config: {
      template: 'welcome_v1'
    }
  }, {
    id: 's2',
    type: 'delay',
    label: 'Wait 2 Hours',
    config: {
      duration: 120
    }
  }, {
    id: 's3',
    type: 'condition',
    label: 'Check: Has Replied?',
    config: {
      condition: 'has_reply'
    }
  }, {
    id: 's4',
    type: 'message',
    label: 'Send Brochure PDF',
    config: {
      template: 'brochure_pdf'
    }
  }],
  stats: {
    enrolled: 1240,
    completed: 850,
    converted: 120
  }
}, {
  id: '2',
  name: 'Test Drive Follow-up',
  active: true,
  trigger: 'Test Drive Completed',
  steps: [{
    id: 's1',
    type: 'delay',
    label: 'Wait 24 Hours',
    config: {
      duration: 1440
    }
  }, {
    id: 's2',
    type: 'message',
    label: 'Ask for Feedback',
    config: {
      template: 'feedback_request'
    }
  }],
  stats: {
    enrolled: 450,
    completed: 420,
    converted: 85
  }
}];
export function WorkflowBuilder() {
  const [workflows, setWorkflows] = useState<Workflow[]>(MOCK_WORKFLOWS);
  const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow | null>(MOCK_WORKFLOWS[0]);
  return <div className="flex h-[calc(100vh-140px)] bg-gray-50 rounded-xl overflow-hidden border border-gray-200" data-id="element-1141">
      {/* Sidebar List */}
      <div className="w-80 bg-white border-r border-gray-200 flex flex-col" data-id="element-1142">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-gray-50" data-id="element-1143">
          <h3 className="font-semibold text-gray-800" data-id="element-1144">Workflows</h3>
          <button className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm" data-id="element-1145">
            <PlusIcon size={18} data-id="element-1146" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto" data-id="element-1147">
          {workflows.map(workflow => <div key={workflow.id} onClick={() => setSelectedWorkflow(workflow)} className={`p-4 border-b border-gray-100 cursor-pointer transition-colors ${selectedWorkflow?.id === workflow.id ? 'bg-blue-50 border-l-4 border-l-blue-600' : 'hover:bg-gray-50 border-l-4 border-l-transparent'}`} data-id="element-1148">
              <div className="flex justify-between items-start mb-1" data-id="element-1149">
                <h4 className={`font-medium ${selectedWorkflow?.id === workflow.id ? 'text-blue-900' : 'text-gray-900'}`} data-id="element-1150">
                  {workflow.name}
                </h4>
                <div className={`w-2 h-2 rounded-full mt-2 ${workflow.active ? 'bg-green-500' : 'bg-gray-300'}`} data-id="element-1151" />
              </div>
              <p className="text-xs text-gray-500 mb-3" data-id="element-1152">{workflow.trigger}</p>
              <div className="flex items-center space-x-4 text-xs text-gray-400" data-id="element-1153">
                <span data-id="element-1154">{workflow.stats.enrolled} Enrolled</span>
                <span data-id="element-1155">{workflow.stats.converted} Converted</span>
              </div>
            </div>)}
        </div>
      </div>

      {/* Builder Canvas */}
      {selectedWorkflow ? <div className="flex-1 flex flex-col bg-gray-50" data-id="element-1156">
          {/* Canvas Header */}
          <div className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6 shadow-sm" data-id="element-1157">
            <div data-id="element-1158">
              <h2 className="text-lg font-bold text-gray-900" data-id="element-1159">
                {selectedWorkflow.name}
              </h2>
              <p className="text-xs text-gray-500" data-id="element-1160">Last edited: 2 hours ago</p>
            </div>
            <div className="flex items-center space-x-3" data-id="element-1161">
              <span className="flex items-center text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-full" data-id="element-1162">
                <span className={`w-2 h-2 rounded-full mr-2 ${selectedWorkflow.active ? 'bg-green-500' : 'bg-gray-400'}`} data-id="element-1163" />
                {selectedWorkflow.active ? 'Active' : 'Inactive'}
              </span>
              <button className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors" data-id="element-1164">
                Edit Settings
              </button>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 shadow-sm transition-colors" data-id="element-1165">
                Save Changes
              </button>
            </div>
          </div>

          {/* Canvas Area */}
          <div className="flex-1 overflow-y-auto p-8 relative" data-id="element-1166">
            <div className="max-w-2xl mx-auto space-y-8 pb-20" data-id="element-1167">
              {/* Trigger Node */}
              <div className="flex flex-col items-center" data-id="element-1168">
                <div className="bg-blue-600 text-white px-6 py-3 rounded-full shadow-md z-10 font-medium flex items-center" data-id="element-1169">
                  <PlayIcon size={16} className="mr-2" data-id="element-1170" />
                  Trigger: {selectedWorkflow.trigger}
                </div>
                <div className="h-8 w-0.5 bg-gray-300" data-id="element-1171"></div>
              </div>

              {/* Steps */}
              {selectedWorkflow.steps.map((step, index) => <div key={step.id} className="flex flex-col items-center group" data-id="element-1172">
                  <div className="w-full bg-white p-5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all relative group-hover:border-blue-300" data-id="element-1173">
                    <div className="absolute right-4 top-4 opacity-0 group-hover:opacity-100 transition-opacity flex space-x-2" data-id="element-1174">
                      <button className="p-1 text-gray-400 hover:text-blue-600" data-id="element-1175">
                        <SettingsIcon size={16} data-id="element-1176" />
                      </button>
                      <button className="p-1 text-gray-400 hover:text-red-500" data-id="element-1177">
                        <TrashIcon size={16} data-id="element-1178" />
                      </button>
                    </div>

                    <div className="flex items-center" data-id="element-1179">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-4 ${step.type === 'message' ? 'bg-green-100 text-green-600' : step.type === 'delay' ? 'bg-orange-100 text-orange-600' : step.type === 'condition' ? 'bg-purple-100 text-purple-600' : 'bg-gray-100 text-gray-600'}`} data-id="element-1180">
                        {step.type === 'message' && <MessageSquareIcon size={20} data-id="element-1181" />}
                        {step.type === 'delay' && <ClockIcon size={20} data-id="element-1182" />}
                        {step.type === 'condition' && <GitBranchIcon size={20} data-id="element-1183" />}
                      </div>
                      <div data-id="element-1184">
                        <h5 className="font-semibold text-gray-900" data-id="element-1185">
                          {step.label}
                        </h5>
                        <p className="text-xs text-gray-500 mt-1" data-id="element-1186">
                          {step.type === 'message' && `Template: ${step.config.template}`}
                          {step.type === 'delay' && `Duration: ${step.config.duration} mins`}
                          {step.type === 'condition' && `If: ${step.config.condition}`}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Connector Line */}
                  {index < selectedWorkflow.steps.length - 1 && <div className="h-8 w-0.5 bg-gray-300" data-id="element-1187"></div>}
                </div>)}

              {/* Add Step Button */}
              <div className="flex flex-col items-center" data-id="element-1188">
                <div className="h-8 w-0.5 bg-gray-300" data-id="element-1189"></div>
                <button className="w-10 h-10 rounded-full bg-white border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 hover:border-blue-500 hover:text-blue-500 transition-colors shadow-sm" data-id="element-1190">
                  <PlusIcon size={20} data-id="element-1191" />
                </button>
              </div>
            </div>
          </div>
        </div> : <div className="flex-1 flex items-center justify-center text-gray-400" data-id="element-1192">
          Select a workflow to edit
        </div>}
    </div>;
}